/**
 * Pure step model for the reseller onboarding wizard (CLA-587).
 *
 * The four-stop rail — Account → Agreement → Stripe → Done — and the resume
 * redirect are all driven by these pure functions so they can be unit-tested
 * without rendering or atoms. Nothing here touches the DOM, Clerk, or the RPC
 * client — the routes/atoms map server RPC state (`resellers.agreement.status`,
 * `resellers.stripe.status`) into these primitives.
 *
 * Two principles carried from the customer onboarding shell: the wizard
 * position lives in the URL (each stop is its own route; refresh lands on the
 * same stop) and completion is DERIVED from server state (no local
 * wizard-progress storage). `account` is complete by definition once the caller
 * is a signed-in reseller; the other stops read their completion from the RPCs.
 *
 * @since 1.0.0
 */
import type { OnboardingStepStatus } from "./onboarding-steps.ts";

export type { OnboardingStepStatus };

/**
 * The four rail stops, in order. `account` is complete the moment the caller is
 * a signed-in reseller (the wizard's guard proves it); `agreement` and `stripe`
 * derive completion from server RPC state; `done` is terminal, complete once
 * both prior stops are.
 *
 * @since 1.0.0
 */
export type ResellerOnboardingRailStepId = "account" | "agreement" | "stripe" | "done";

/**
 * Ordered rail definition — id + human label. Order is significant: it drives
 * both the rendered top-to-bottom rail and the resume resolver's "first
 * incomplete stop" search.
 *
 * @since 1.0.0
 */
export const RESELLER_ONBOARDING_RAIL_STEPS: ReadonlyArray<
  { readonly id: ResellerOnboardingRailStepId; readonly label: string }
> = [
  { id: "account", label: "Account" },
  { id: "agreement", label: "Agreement" },
  { id: "stripe", label: "Stripe" },
  { id: "done", label: "Done" },
];

/**
 * Base path for every reseller onboarding route. The `reseller_` directory
 * escapes the reseller dashboard layout (`reseller.tsx`) via TanStack's
 * trailing-underscore rule, so the wizard renders in its OWN shell while the
 * URL still reads `/clawinstall/reseller/onboarding`.
 *
 * @since 1.0.0
 */
export const RESELLER_ONBOARDING_BASE_PATH = "/clawinstall/reseller/onboarding";

/**
 * Route path for a rail stop, e.g. `/clawinstall/reseller/onboarding/agreement`.
 * The `account` stop has no page of its own (it is complete by definition once
 * the caller is a signed-in reseller), so it maps to the wizard base path.
 *
 * @since 1.0.0
 */
export const resellerOnboardingStepPath = (id: ResellerOnboardingRailStepId): string =>
  id === "account" ? RESELLER_ONBOARDING_BASE_PATH : `${RESELLER_ONBOARDING_BASE_PATH}/${id}`;

/**
 * Where the terminal Done stop hands the reseller off: their existing reseller
 * dashboard. Plain `string` so it flows into `<a href>`/`<Link to>` without
 * TanStack literal-route validation.
 *
 * @since 1.0.0
 */
export const RESELLER_DASHBOARD_PATH = "/clawinstall/reseller" as const;

/**
 * Where the wizard's own auth guard sends a caller who is authenticated but is
 * NOT a reseller: the customer dashboard. (An unauthenticated caller resolves
 * to the same redirect — they have no reseller membership either.)
 *
 * @since 1.0.0
 */
export const NON_RESELLER_REDIRECT_PATH = "/clawinstall/dashboard" as const;

/**
 * Server-derived completion of each rail stop. `account` is always `true`
 * inside the wizard (the guard admits only signed-in resellers); `agreement`
 * and `stripe` come from the RPC atoms; `done` is the conjunction. Kept as a
 * plain record so the rail status + resume resolver stay pure.
 *
 * @since 1.0.0
 */
export interface ResellerStepCompletion {
  readonly account: boolean;
  readonly agreement: boolean;
  readonly stripe: boolean;
  readonly done: boolean;
}

/**
 * Fold the two independent completion signals into the full record: `done` is
 * complete exactly when both the agreement and Stripe stops are. `account` is
 * always complete within the guarded wizard.
 *
 * @since 1.0.0
 */
export const resellerStepCompletion = (
  { agreement, stripe }: { readonly agreement: boolean; readonly stripe: boolean },
): ResellerStepCompletion => ({
  account: true,
  agreement,
  stripe,
  done: agreement && stripe,
});

/**
 * Completed/current/upcoming for a stop. Completion is driven by SERVER state
 * (`completion`), never by position.
 *
 * SERVER COMPLETION WINS OVER "current": a stop the server marks complete reads
 * `completed` (✓) even while it is the stop the caller is viewing. This is what
 * flips the Agreement stop to ✓ the instant the accept read refreshes — without
 * a route change — and what keeps the Stripe stop's ✓ on Stripe (not pushed onto
 * Done) when the caller lands back on `/stripe?state=return` connected (the
 * 2026-07-06 off-by-one, CLA-591). Only a NOT-yet-complete stop shows the
 * `current` ring for the route the caller is on; every other incomplete stop is
 * `upcoming`.
 *
 * `done` is the terminal hand-off: reaching it IS the finish line, so it carries
 * no in-wizard ✓ — it renders `current` while viewed and `upcoming` (numbered)
 * otherwise, never `completed`. (Its `completion.done` signal still drives the
 * resume resolver; it just never paints a check on the rail.) Without this, an
 * all-complete wizard on `/stripe` painted a ✓ on Done while Stripe showed its
 * number — the exact off-by-one this fixes.
 *
 * @since 1.0.0
 */
export const resellerRailStepStatus = (
  current: ResellerOnboardingRailStepId,
  step: ResellerOnboardingRailStepId,
  completion: ResellerStepCompletion,
): OnboardingStepStatus => {
  if (step === "done") return step === current ? "current" : "upcoming";
  if (completion[step]) return "completed";
  return step === current ? "current" : "upcoming";
};

/**
 * The rail stop a wizard path is viewing, from its last segment. The bare base
 * path (no trailing stop) is the `account` stop; an unknown segment also falls
 * back to `account` so the rail always has a sensible current marker.
 *
 * @since 1.0.0
 */
export const resellerCurrentStepForPath = (pathname: string): ResellerOnboardingRailStepId => {
  const segment = pathname.split("/").filter((part) => part.length > 0).pop();
  const match = RESELLER_ONBOARDING_RAIL_STEPS.find((step) => step.id === segment);
  return match?.id ?? "account";
};

/**
 * Resolve the resume stop from server-derived completion alone (zero local
 * storage): the FIRST incomplete stop, so a fresh load lands the reseller where
 * they left off. When every stop is complete the wizard resumes at `done`. This
 * is a landing redirect (like the customer resume resolver), NOT the banned
 * "auto-navigation between steps" — once on a stop, completing it never yanks
 * the reseller forward.
 *
 * @since 1.0.0
 */
export const resolveResellerResumeStep = (
  completion: ResellerStepCompletion,
): ResellerOnboardingRailStepId => {
  const firstIncomplete = RESELLER_ONBOARDING_RAIL_STEPS.find((step) => !completion[step.id]);
  return firstIncomplete?.id ?? "done";
};

/**
 * The Stripe stop's return/refresh landing modes, carried in its `?state=`
 * search param. Stripe's hosted onboarding returns to the Stripe route with
 * `state=return` (finished/abandoned the flow) or `state=refresh` (the
 * single-use account link expired — mint a fresh one). Any other value reads as
 * a plain first visit (`null`).
 *
 * @since 1.0.0
 */
export type ResellerStripeReturnState = "return" | "refresh";

/**
 * Narrow the Stripe route's `?state=` search param to a known landing mode, or
 * `null` for a plain first visit. Kept pure so the route stays free of a Schema
 * dependency and the validator is trivially unit-testable.
 *
 * @since 1.0.0
 */
export const resellerStripeReturnState = (value: unknown): ResellerStripeReturnState | null =>
  value === "return" || value === "refresh" ? value : null;
